import { CSSProperties } from 'react'

import { GemType } from '~/components/Rock/types'
import { EventInfo } from '~/gamification/types/gamification.types'
import { CategoryPathUrls, Tag, prerequisite } from '~/bootcamp/types/bootcamp.types'
import { BadgeImage, SuperBadgePartImage, SuperBadgePartImagesProgress } from '~/shared/types/bootcampBadge.types'
import { CategorizationQuestion } from '~/bootcamp/Activities/SanityActivities/Categorization/types/Categorization.type'
import { OrderElementsQuestion } from '~/bootcamp/Activities/SanityActivities/OrderElements/types/OrderElements.types'
import { BlockContent } from '~/bootcamp/Activities/types/activities.types'
import { InAppKeyboardLayoutId } from '~/in-app-keyboard/types/inAppKeyboard.types'

import { FileTopic } from './theory'

export enum BootCampStatus {
  START = 'unknown',
  IN_PROGRESS = 'in progress',
  COMPLETED = 'completed',
  COMING = 'coming',
  PUBLISHED = 'published',
}

export interface BootCampModuleCard {
  cardIndex: string | number
  className?: string
  collectedChests?: string | number
  icon?: string
  onStart?: () => void
  path: string
  progress?: number
  status: BootCampStatus
  style?: CSSProperties
  title: string
  totalChests?: string | number
  totalLevels: number
}

export interface ModuleItem extends BootCampModuleCard {
  position: {
    top: number
    left: number
  }
  key?: string
}

export type BootcampPages = Array<{ activities?: Array<{ difficulties?: number[] }> }>

export interface ChestsStatus {
  collected: number
  total: number
}
export interface TagStatus {
  tag: Tag.NEW | Tag.UPDATED
  validity: string
}

/**
 * This type to get data from sanity
 * This contains all objects for single bootcamp
 * Main User: Sanity Client
 */
interface BootcampItemCommon {
  name: string // Bootcamp Name
  key: string // URL Key
  modules: number // count of modules
  serial: number // Serial of Bootcamp
  tagStatus?: TagStatus
  isUpcoming?: boolean // Upcoming subjects in other locale than English
}
export interface BootCampItem extends BootcampItemCommon {
  superBadgeOutlineImage: BadgeImage
  superBadgePartImages: SuperBadgePartImagesProgress[]
  hasProgress: boolean
  progress: number
}

// This schema is for GROQ Query
export interface BootCampItemGroq extends BootcampItemCommon {
  chests?: Array<{ moduleNumber: string; pages?: BootcampPages }>
  superBadgeOutlineImage: BadgeImage // Super Badge Outline/Shadow image
  superBadgePartImages: SuperBadgePartImage[] // Super Badge Part Images
  courses?: bootcampOverviewGroq
}

export interface SubjectsListGroq {
  subjects: BootCampItemGroq[]
  reference: Array<{
    key: string
    superBadgeOutlineImage: BadgeImage
    name: string
  }>
}

/**
 * Used for bootcamp overview
 */
interface bootcampOverviewModuleCommon {
  badgeImage: BadgeImage // Sub Badge Image
  badgeOutlineImage: BadgeImage // Sub Badge Shadow/Outline Image
  name?: string // Name of the module
  moduleNumber: string // Module name
  levels: number // Count of levels
  progress: number // Progress of bootcamp
  prerequisites?: prerequisite[] // Module prerequisites
  tagStatus?: TagStatus
  isUpcoming?: boolean // Upcoming bootcamps in other locale than English
  taskReferences: { mathPreview: { srcKey: string; _type: string } }
}

export interface bootcampOverviewModule extends bootcampOverviewModuleCommon {
  bossDefeated: boolean // Will toggle Bootcamp collectable state
  chests: ChestsStatus
  hasWhyLevel?: boolean
  firstPageData?: PageLevel
}

export interface bootcampOverviewModuleGroq extends bootcampOverviewModuleCommon {
  hasWhyLevel: boolean
  firstPageData: PageLevel
  chests: BootcampPages
}

/**
 * To get bootcampOverview Design Data
 * Main user: Sanity Client
 */
interface bootcampOverviewCommon {
  name: string // Name of the Bootcamp
  optionalTitle?: string // Optional title for Bootcamp
  intro?: unknown[] // Bootcamp intro data block object: https://www.sanity.io/docs/block-type
}
export interface bootcampOverview extends bootcampOverviewCommon {
  modules?: bootcampOverviewModule[] // All modules Data
}

export interface bootcampOverviewGroq extends bootcampOverviewCommon {
  metaDescription: string
  modules?: bootcampOverviewModuleGroq[] // All modules Data
}

export interface bootcampsListGroq {
  bootcamps: bootcampOverviewGroq
  reference: {
    modules: Array<{
      mathPreview: { mathPreview: { srcKey: string; _type: string } }
      moduleNumber: string
      badgeOutlineImage: BadgeImage
      hasWhyLevel: boolean
      firstPageData: PageLevel
      name: string
    }>
  }
}

export interface asset {
  help?: {
    encyclopedia: string
    mathDrill: string
  }
  chest?: {
    funfact?: unknown[]
    event?: EventInfo
  }
  usefulWords?: Array<{
    term: string
    link?: string
    definition?: unknown[]
  }>
}

export enum ActivityQuestionType {
  multipleChoiceQuestion = 'multipleChoiceQuestion',
  fillInTheBlanks = 'fillInTheBlanks',
  circleCombinations = 'circleCombinations',
  categorization = 'categorization',
  interactive = 'interactive',
  matchingPairs = 'matchingPairs',
  orderElements = 'orderElements',
  measureTheItem = 'measureTheItem',
  numberLine = 'numberLine',
}

export enum ContentType {
  bootcampVideo = 'bootcampVideo',
  curriculumVideo = 'curriculumVideo',
}

export enum MultipleChoiceOptionsLayout {
  twoColumn = 'twoColumn',
  oneColumn = 'oneColumn',
  oneRow = 'oneRow',
}

export interface Difficulty {
  questionType: ActivityQuestionType
  activityName: string // Respective component name
  description?: unknown[] // Block Content
  difficultyLevel: number // Difficulty level
  rewards: number // Number of rewards for difficulty level
  multipleChoiceActivity: MultipleChoiceQuestion[]
  fillInTheBlankActivity: FillInTheBlankQuestion[]
  circleCombinationsActivity: CircleCombinationsQuestion[]
  categorizationActivity: CategorizationQuestion[]
  matchingPairsActivity: MatchingPairsQuestion[]
  orderElementsActivity: OrderElementsQuestion[]
  numberLineActivity: NumberLineQuestion[]
  measureTheItemActivity: MeasureTheItemQuestion[]
  keyboardLayout: InAppKeyboardLayoutId | null // Keyboard layout for fill in the blanks type activity
}

export interface MultipleChoiceQuestion {
  showOptions: number
  maximumRightAnswer: number
  shouldShuffle: boolean
  optionsLayout: MultipleChoiceOptionsLayout
  question: unknown[] // Block Content
  options: MultipleChoiceOption[]
  questionId: string
  _key: string // Uniquely defines a object
}

export interface FillInTheBlankQuestion {
  question: unknown[] // Block Content
  areInputsDependent: boolean // If inputs depend on each other
  questionId: string
  inputs: Array<{
    label: unknown[] // Block Content
    answers: string[]
    inputType: FillInTheBlankInputType
    _key: string // Unique identifier
  }>
}

export enum FillInTheBlankInputType {
  TEXT = 'text',
  INTEGER = 'integer',
  FLOAT = 'float',
}

export interface MultipleChoiceOption {
  isCorrect: boolean
  option: unknown[] // Block Content
  _key: string // Uniquely defines a object
}

export interface CircleCombinationsQuestion {
  circleElements: string[]
  randomizeCircleElementsOrder: boolean
  validCircleCombinations: string[]
  combinationsToFind: number
  questionId: string
  _key: string // Uniquely defines a object
}

export interface MatchingPairsQuestion {
  pairs: Array<{ element1: BlockContent[]; element2: BlockContent[]; _key?: string }>
  questionId: string
  _key: string // Uniquely defines a object
}

export interface MeasureTheItemQuestion {
  measureElement: {
    alt: string
    srcKey: string
  }
  imageHeight: number
  imageWidth: number
  questionId: string
  _key: string // Uniquely defines a object
}

export interface NumberLineQuestion {
  questionExpression: string
  setTicksManually: boolean // If true then set ticks manually in Sanity
  leftMostValue: number
  rightMostValue: number
  numberOfTicks: number
  startTick: number // Value of the first tick
  answerValue: number
  showValueOfEveryNthTick: number // Show value of every nth tick
  ticks: bootcampNumberLineActivityTick[]
  questionId: string
  _key: string // Uniquely defines a object
}

export interface bootcampNumberLineActivityTick {
  tickValue: string
  large: boolean
  isDefaultTick: boolean
  isAnswerTick: boolean
}

export interface Activity {
  difficulties: Difficulty[]
  type: number
  gemType: GemType
  workLoadLevel: number
}

export interface PageLevel {
  level: string
  regular: boolean // If true then has both leaf else only upper (definition leaf)
  shortTitle?: string
  hasEncyclopedia?: boolean
}

// For bootcamp Page (How & Why)
export interface BootcampPage {
  valid: boolean // If false then redirect to 404
  activities?: Activity[]
  asset?: asset
  blockContent?: unknown[] // How or Why content block
  levelCount?: number
  levelsPageData?: PageLevel[]
  levelNo?: string // Current level No
  moduleCount?: number // Total module count of parent bootcamp
  moduleName?: string
  sectionName?: string // Section name of Why & How block
  whyShortTitle?: string // Short Title of Why block
  moduleNo?: string // Current Parent module
  metaDescription?: string // meta description for SEO
  subjectName?: string // Related subject Name
  levelActivities?: SanityLevelActivities[]
  complexityLevel?: number // Overall complexity of a bootcamp
  hasWhyLevel?: boolean
  badgeImage?: BadgeImage
  badgeOutlineImage?: BadgeImage
  encyclopediaArticle: FileTopic | null
  levelIds?: string[]
}

export interface VccModuleData {
  assets?: asset
  complexityLevel?: number
  hasWhyLevel?: boolean
  howContent?: unknown[]
  levelsPageData?: PageLevel[]
  levelActivities?: SanityLevelActivities[]
  levelCount?: number
  metaDescription?: string
  moduleNumber?: string
  name?: string
  page: VccModulePage[]
  shortTitle?: string
  whyName?: string
  badgeImage?: BadgeImage
  badgeOutlineImage?: BadgeImage
  firstPageData?: PageLevel
  encyclopediaArticle: string | null
}
interface VccModulePage {
  activities?: Activity[]
  assets?: asset
  howContent?: unknown[] // How or Why content block
  level: string
  isDefinition?: boolean
  metaDescription?: string
  metaDescriptionActivity?: string
  name: string
  encyclopediaArticle: FileTopic | null
}
export interface BootcampChestsData {
  level: string
  activities?: BootcampChestsActivityData[] | null
}

export interface BootcampChestsActivityData {
  type?: number
  difficulties: number[]
}

export interface BootcampGymsListUserData {
  key: string
  chests: number
  moduleId: string
  bossDefeated: boolean
}

export interface BootcampOverviewUserData {
  moduleId: string
  bossDefeated: boolean
  chests: number
  bootcampId?: string
}

type CategoryTitleTransKeys =
  | 'algebra'
  | 'geometry'
  | 'numbersAndQuantities'
  | 'statisticsAndProbability'
  | 'functions'
  | 'proofs'

export interface VideoTopic {
  title: string
  titleTransKey: CategoryTitleTransKeys
  path: string
  noOfSubjects: number
  noOfCompletedSubjects: number
  noOfActivities: number
  noOfVideos: number
  noOfActivitiesEn?: number
  noOfVideosEn?: number
  noOfWatchedVideos: number
  categoryThemeColor: string
  categoryFontColor: string
  noOfChestsInGyms: Array<{ key: string; totalChests: number }>
  isPublished: boolean
  isUpcoming: boolean // Is available in English locale
  key: CategoryPathUrls // Category url key
}
export interface SanityLevelActivities {
  level: string
  numOfActivities: string
}

export interface LevelActivities {
  [level: string]: number
}
